import { useQuery } from '@tanstack/react-query';
import {
  areaLevelReaches,
  formatMoney,
  highestAreaLevel,
  isOpenJob,
  JOB_STATE_LABELS,
  JOB_STATES,
  type JobIndicatorSummary,
  type JobKind,
  type JobListItem,
  type JobState,
  type Light,
  type PendingOffer,
} from '@k-management/shared';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useCan, useCurrentUser } from '../../auth/AuthProvider.js';
import { DataGrid, type GridColumn } from '../../components/DataGrid.js';
import { type Command, useCommands, usePageTitle } from '../../components/layout/page-chrome.js';
import { apiRequest } from '../../lib/api.js';
import { formatDate } from '../../lib/format.js';
import { useListFilters } from '../../lib/list-filters.js';
import { JobDialog } from './JobDialog.js';
import { jobKeys, jobPath, JobStatePill, Meter, namesOf, overBudget } from './jobs-ui.js';
import { LinkOfferDialog } from './LinkOfferDialog.js';

const TITLES: Record<JobKind, string> = { commessa: 'Commesse', progetto: 'Progetti' };

/**
 * Commesse and progetti (area Operations, 2.41), 16/10/2026: the same list, by
 * kind. Who changes the commesse also sees the accepted offers that wait for
 * their commessa. The progetti show their area and how much of the budget of
 * the costs is spent (26/10/2026).
 */
export function JobsListPage({ kind }: { kind: JobKind }) {
  usePageTitle(TITLES[kind]);
  const navigate = useNavigate();
  const user = useCurrentUser();
  const module = useCan('commesse.write');
  const moduleAdmin = useCan('commesse.admin');
  // A progetto is also created by who has an area with «Modifica» (ADR-025).
  const canWrite =
    module ||
    (kind === 'progetto' && areaLevelReaches(highestAreaLevel(user.projectAreas), 'write'));
  const [creating, setCreating] = useState(false);
  const { filters, setFilter, reset, changed, changedClass } = useListFilters(`jobs-${kind}`, {
    state: 'aperte' as 'aperte' | 'tutte' | JobState,
    area: '' as string,
  });
  const jobs = useQuery({
    queryKey: jobKeys.list(kind),
    queryFn: () => apiRequest<JobListItem[]>(`/jobs?kind=${kind}`),
  });
  // The indicators of the open ones (2.45): read apart, the list does not wait for them.
  const indicators = useQuery({
    queryKey: [...jobKeys.all, 'indicators'],
    queryFn: () => apiRequest<JobIndicatorSummary[]>('/jobs/indicators'),
  });
  const indicatorOf = (job: JobListItem) => indicators.data?.find((row) => row.jobId === job.id);
  useCommands([
    ...(canWrite
      ? [
          {
            key: 'new',
            label: kind === 'commessa' ? 'Nuova commessa' : 'Nuovo progetto',
            icon: 'plus',
            primary: true,
            onClick: () => setCreating(true),
          } satisfies Command,
        ]
      : []),
    ...(kind === 'progetto' && moduleAdmin
      ? [
          {
            key: 'areas',
            label: 'Aree e accessi',
            icon: 'users',
            onClick: () => void navigate('/progetti/aree'),
          } satisfies Command,
        ]
      : []),
  ]);
  const areas = [
    ...new Map(
      (jobs.data ?? []).flatMap((job) =>
        job.areaCode ? [[job.areaCode, job.areaName ?? job.areaCode] as const] : [],
      ),
    ),
  ].sort((a, b) => a[1].localeCompare(b[1]));
  const filtered = (jobs.data ?? []).filter(
    (job) =>
      (filters.state === 'tutte'
        ? true
        : filters.state === 'aperte'
          ? isOpenJob(job.state)
          : job.state === filters.state) &&
      (!filters.area || (filters.area === '-' ? !job.areaCode : job.areaCode === filters.area)),
  );
  // Each madre followed by its sottocommesse (17/10/2026); a figlia whose madre
  // is not in the list stands on its own.
  const shown = new Set(filtered.map((job) => job.id));
  const nested = (job: JobListItem) => job.parentId !== null && shown.has(job.parentId);
  const rows = filtered
    .filter((job) => !nested(job))
    .flatMap((job) => [
      job,
      ...filtered
        .filter((child) => child.parentId === job.id)
        .sort((a, b) => a.code.localeCompare(b.code)),
    ]);
  const columns: GridColumn<JobListItem>[] = [
    {
      key: 'code',
      label: 'Codice',
      required: true,
      value: (job) => job.code,
      render: (job) =>
        nested(job) ? <span className="job-child">{job.code}</span> : <strong>{job.code}</strong>,
    },
    {
      key: 'name',
      label: kind === 'commessa' ? 'Nome' : 'Titolo',
      value: (job) => job.name,
      render: (job) => job.name,
    },
    ...(kind === 'progetto'
      ? [
          {
            key: 'area',
            label: 'Area',
            value: (job: JobListItem) => job.areaName,
            render: (job: JobListItem) => job.areaName ?? <span className="muted">senza area</span>,
          },
        ]
      : []),
    ...(kind === 'commessa'
      ? [
          {
            key: 'client',
            label: 'Cliente',
            value: (job: JobListItem) => job.clientName,
            render: (job: JobListItem) => job.clientName ?? '—',
          },
        ]
      : []),
    {
      key: 'pm',
      label: 'PM',
      value: (job) => namesOf(job.people, 'pm'),
      render: (job) => namesOf(job.people, 'pm'),
    },
    ...(kind === 'commessa'
      ? [
          {
            key: 'account',
            label: 'Account',
            value: (job: JobListItem) => namesOf(job.people, 'account'),
            render: (job: JobListItem) => namesOf(job.people, 'account'),
          },
          {
            key: 'commerciale',
            label: 'Commerciale',
            hiddenByDefault: true,
            value: (job: JobListItem) => namesOf(job.people, 'commerciale'),
            render: (job: JobListItem) => namesOf(job.people, 'commerciale'),
          },
          {
            key: 'offers',
            label: 'Offerte',
            align: 'right' as const,
            value: (job: JobListItem) => job.offerCount,
            render: (job: JobListItem) => job.offerCount,
          },
          {
            key: 'net',
            label: 'Imponibile',
            align: 'right' as const,
            value: (job: JobListItem) => Number(job.offersNet),
            render: (job: JobListItem) => formatMoney(job.offersNet),
          },
        ]
      : [
          {
            key: 'budget',
            label: 'Budget costi',
            align: 'right' as const,
            value: (job: JobListItem) => Number(job.costs?.budget ?? 0),
            render: (job: JobListItem) => (job.costs ? formatMoney(job.costs.budget) : '—'),
          },
          {
            key: 'actual',
            label: 'Consuntivo',
            align: 'right' as const,
            value: (job: JobListItem) => Number(job.costs?.actual ?? 0),
            render: (job: JobListItem) => (job.costs ? formatMoney(job.costs.actual) : '—'),
          },
          {
            key: 'spent',
            label: 'Speso',
            value: (job: JobListItem) =>
              job.costs && Number(job.costs.budget) > 0
                ? Number(job.costs.actual) / Number(job.costs.budget)
                : null,
            render: (job: JobListItem) =>
              job.costs && Number(job.costs.budget) > 0 ? (
                <Meter
                  share={Number(job.costs.actual) / Number(job.costs.budget)}
                  over={overBudget(job.costs)}
                />
              ) : (
                '—'
              ),
          },
        ]),
    {
      key: 'dates',
      label: 'Inizio – fine',
      value: (job) => job.startOn,
      render: (job) =>
        job.startOn || job.endOn
          ? `${job.startOn ? formatDate(job.startOn) : '…'} – ${job.endOn ? formatDate(job.endOn) : '…'}`
          : '—',
    },
    {
      key: 'progress',
      label: 'Avanz.',
      align: 'right',
      value: (job) => indicatorOf(job)?.percent ?? null,
      render: (job) => {
        const row = indicatorOf(job);
        if (row?.percent == null) return '—';
        return (
          <>
            {row.percent.toLocaleString('it-IT', { maximumFractionDigits: 1 })} %
            {row.source === 'ore' && <small className="muted"> ore</small>}
          </>
        );
      },
    },
    {
      key: 'spi',
      label: 'SPI',
      value: (job) => indicatorOf(job)?.spi.value ?? null,
      render: (job) => <IndicatorValue value={indicatorOf(job)?.spi} />,
    },
    {
      key: 'cpi',
      label: 'CPI',
      value: (job) => indicatorOf(job)?.cpi?.value ?? null,
      render: (job) => <IndicatorValue value={indicatorOf(job)?.cpi} />,
    },
    ...(kind === 'commessa'
      ? [
          {
            key: 'marginAtEnd',
            label: 'Margine a finire',
            value: (job: JobListItem) => indicatorOf(job)?.marginAtEnd?.percent ?? null,
            render: (job: JobListItem) => (
              <IndicatorValue value={indicatorOf(job)?.marginAtEnd} suffix=" %" />
            ),
          },
        ]
      : []),
    {
      key: 'risks',
      label: 'Rischi',
      align: 'right',
      value: (job) => indicatorOf(job)?.risks.open ?? null,
      render: (job) => {
        const risks = indicatorOf(job)?.risks;
        return <IndicatorValue value={risks ? { light: risks.light, value: risks.open } : null} />;
      },
    },
    {
      key: 'state',
      label: 'Stato',
      value: (job) => JOB_STATES.indexOf(job.state),
      render: (job) => (
        <>
          <JobStatePill state={job.state} />
          {kind === 'progetto' && overBudget(job.costs) && (
            <>
              {' '}
              <span className="pill bad">Oltre il budget</span>
            </>
          )}
        </>
      ),
    },
  ];

  return (
    <section className="stack">
      <div className="page-header">
        <div>
          <h1>{TITLES[kind]}</h1>
          <p className="muted">
            {kind === 'commessa'
              ? 'Il lavoro con un cliente: raccoglie le offerte accettate nel tempo. PM e Account vedono tutta la commessa, il commerciale solo se abilitato.'
              : 'Le attività interne di K-Rev, senza cliente: evoluzioni dell’app, marketing, sito… Ogni progetto ha un PM, un budget dei costi e un obiettivo con i suoi indicatori.'}
          </p>
        </div>
      </div>
      {kind === 'commessa' && canWrite && <PendingOffers />}
      {jobs.isError && <p className="error">{jobs.error.message}</p>}
      {jobs.data && (
        <DataGrid
          id={`jobs-${kind}`}
          filtersChanged={changed}
          onResetFilters={reset}
          columns={columns}
          rows={rows}
          rowKey={(job) => job.id}
          groups={{
            of: (job) =>
              nested(job) ? job.parentId : job.childCount > 0 && !job.parentId ? job.id : null,
            isHead: (job) => job.childCount > 0 && !nested(job),
          }}
          onRowOpen={(job) => void navigate(jobPath(job))}
          openOnClick
          searchPlaceholder={kind === 'commessa' ? 'Codice, nome o cliente' : 'Codice, titolo o PM'}
          toolbar={
            <>
              {kind === 'progetto' && (
                <select
                  className={changedClass('area')}
                  value={filters.area}
                  onChange={(event) => setFilter('area', event.target.value)}
                  aria-label="Area"
                >
                  <option value="">Tutte le aree</option>
                  {areas.map(([code, name]) => (
                    <option key={code} value={code}>
                      {name}
                    </option>
                  ))}
                  <option value="-">Senza area</option>
                </select>
              )}
              <select
                className={changedClass('state')}
                value={filters.state}
                onChange={(event) => setFilter('state', event.target.value as typeof filters.state)}
                aria-label="Stato"
              >
                <option value="aperte">{kind === 'commessa' ? 'Aperte' : 'Aperti'}</option>
                <option value="tutte">Tutti gli stati</option>
                {JOB_STATES.map((state) => (
                  <option key={state} value={state}>
                    {JOB_STATE_LABELS[state]}
                  </option>
                ))}
              </select>
            </>
          }
          empty={
            kind === 'commessa'
              ? 'Nessuna commessa: nascono dalle offerte accettate o con «Nuova commessa».'
              : canWrite
                ? 'Nessun progetto: crealo con «Nuovo progetto».'
                : 'Nessun progetto.'
          }
        />
      )}
      {creating && (
        <JobDialog
          job={null}
          kind={kind}
          onClose={() => setCreating(false)}
          onSaved={(saved) => void navigate(jobPath(saved))}
        />
      )}
    </section>
  );
}

/** The accepted offers without their commessa: who changes the commesse links them. */
function PendingOffers() {
  const navigate = useNavigate();
  const [linking, setLinking] = useState<PendingOffer | null>(null);
  const pending = useQuery({
    queryKey: jobKeys.pending,
    queryFn: () => apiRequest<PendingOffer[]>('/jobs/pending-offers'),
  });
  const rows = pending.data ?? [];
  if (rows.length === 0) return null;
  return (
    <section className="card stack">
      <h2>
        Offerte accettate da collegare <span className="count-pill">{rows.length}</span>
      </h2>
      <p className="muted small">
        Ogni offerta accettata entra in una commessa del suo cliente: una nuova o una già aperta.
      </p>
      <div className="table-scroll">
        <table className="lines">
          <thead>
            <tr>
              <th>Offerta</th>
              <th>Cliente</th>
              <th>Commerciale</th>
              <th>Data</th>
              <th className="right">Imponibile</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((offer) => (
              <tr key={offer.quoteId}>
                <td>
                  <Link to={`/offerte/${offer.quoteId}`}>{offer.label}</Link>
                  {offer.referenceName && <small className="muted"> {offer.referenceName}</small>}
                </td>
                <td>{offer.clientName}</td>
                <td>{offer.owner?.name ?? '—'}</td>
                <td>{formatDate(offer.date)}</td>
                <td className="right">{formatMoney(offer.net)}</td>
                <td className="right">
                  <button type="button" className="primary" onClick={() => setLinking(offer)}>
                    Collega…
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {linking && (
        <LinkOfferDialog
          offer={linking}
          onClose={() => setLinking(null)}
          onLinked={(job) => void navigate(jobPath(job))}
        />
      )}
    </section>
  );
}

const LIGHT_CLASS: Record<Light, string> = { verde: 'green', giallo: 'yellow', rosso: 'red' };

/** A figure of the indicators with its semaforo, or a dash. */
function IndicatorValue({
  value,
  suffix = '',
}: {
  value: { light: Light | null; value?: number | null; percent?: number | null } | null | undefined;
  suffix?: string;
}) {
  const figure = value?.value ?? value?.percent ?? null;
  if (!value || figure === null) return <span className="muted">—</span>;
  const text = `${figure.toLocaleString('it-IT', { maximumFractionDigits: 2 })}${suffix}`;
  return value.light ? (
    <span className={`margin-light ${LIGHT_CLASS[value.light]}`}>
      <span className="dot" />
      {text}
    </span>
  ) : (
    <>{text}</>
  );
}
